import React, { createContext, useContext, useState, useEffect } from 'react';

const VideoContext = createContext();

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};

// Load initial state from localStorage or use defaults
const getInitialState = () => {
  try {
    const saved = localStorage.getItem('barkbacks_video_wizard');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading saved wizard data:', error);
  }

  return {
    // Wizard data
    petName: '',
    photoUrl: '',
    useBreedLibrary: true,
    selectedBreed: '',
    emotion: '',
    activities: [],

    // Daycare info
    daycareId: '',
    daycareName: '',
    logoUrl: '',
    brandColor: '#FF6B35',

    // Video status
    jobId: '',
    status: '',  // pending, processing, complete, failed
    videoUrl: '',
    transcript: '',

    // UI state
    currentStep: 1,
    totalSteps: 7,
  };
};

export const VideoProvider = ({ children }) => {
  const [videoData, setVideoData] = useState(getInitialState);

  // Save to localStorage whenever videoData changes
  useEffect(() => {
    try {
      localStorage.setItem('barkbacks_video_wizard', JSON.stringify(videoData));
    } catch (error) {
      console.error('Error saving wizard data:', error);
    }
  }, [videoData]);

  const updateVideoData = (updates) => {
    setVideoData(prev => ({ ...prev, ...updates }));
  };

  const resetVideoData = () => {
    const resetData = {
      petName: '',
      photoUrl: '',
      useBreedLibrary: true,
      selectedBreed: '',
      emotion: '',
      activities: [],
      daycareId: videoData.daycareId,  // Keep daycare info
      daycareName: videoData.daycareName,
      logoUrl: videoData.logoUrl,
      brandColor: videoData.brandColor,
      jobId: '',
      status: '',
      videoUrl: '',
      transcript: '',
      currentStep: 1,
      totalSteps: 7,
    };
    setVideoData(resetData);
    // Also clear localStorage
    try {
      localStorage.removeItem('barkbacks_video_wizard');
    } catch (error) {
      console.error('Error clearing wizard data:', error);
    }
  };

  const nextStep = () => {
    setVideoData(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, prev.totalSteps),
    }));
  };

  const prevStep = () => {
    setVideoData(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }));
  };

  const value = {
    videoData,
    updateVideoData,
    resetVideoData,
    nextStep,
    prevStep,
  };

  return (
    <VideoContext.Provider value={value}>
      {children}
    </VideoContext.Provider>
  );
};
